import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useDriverHeartbeat } from '../../hooks/useDriverHeartbeat';
import { formatOrderStatus } from '../../utils/orderStatus';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useFlag } from '../../context/FeatureFlagsContext';
import { KitItemBar, kitTotalValue } from '../../components/KitItemBar';
import { Navbar } from '../../components/Navbar';
import { PushSubscribeButton } from '../../components/PushSubscribeButton';
import { DriverPaymentQRModal } from '../../components/DriverPaymentQRModal';
import { formatOrderTimestamp } from '../../utils/orderTimestamp';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Package, 
  User, 
  MapPin,
  Clock,
  Truck,
  CheckCircle,
  Navigation,
  Phone,
  Radio,
  RadioReceiver,
  Power,
  PowerOff,
  QrCode
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function DriverDashboard() {
  const { user } = useAuth();
  const isDriver = user?.role === 'driver';
  // Phase 5.9.21 — VIEW AS DRIVER preview detection. When operator
  // (super_admin / dispatcher) lands here via `/driver?preview=1`, all
  // delivery-mutating actions are blocked with a toast and a sticky banner
  // is rendered. Cookies + auth stay intact across the navigation.
  const isPreviewMode = (() => {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('preview') === '1';
    } catch (_) { return false; }
  })();
  const previewBlock = (label) => toast.warning(
    `Preview mode — ${label} disabled. Return to dispatch to take action.`,
    { duration: 2500, id: 'driver-preview-block' },
  );
  const { online: heartbeatOnline, enabled: heartbeatEnabled, lastLocation: heartbeatLoc, toggleOnline } = useDriverHeartbeat(isDriver && !isPreviewMode);
  const kitsEnabled = useFlag('kits.enabled', true);
  const [orders, setOrders] = useState([]);
  const [myKit, setMyKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingActive, setTrackingActive] = useState({});
  const [lastLocation, setLastLocation] = useState({});
  const trackingIntervals = useRef({});
  const watchIds = useRef({});
  // Phase 5.5-Lite — driver-side payment QR modal
  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrRender, setQrRender] = useState(null);
  const [qrOrder, setQrOrder] = useState(null);  // {id, total, status}
  const [paymentRouting, setPaymentRouting] = useState(null);  // {handle, display_name, show_routing_to_drivers}

  // Feb 2026 (Phase B) — Wake Lock while ANY order is out-for-delivery.
  // Keeps the screen on so watchPosition never pauses. Released the moment
  // the driver has zero active deliveries. See /app/frontend/src/hooks/useWakeLock.js.
  const hasActiveDelivery = useMemo(
    () => orders.some((o) => o.status === 'out_for_delivery'),
    [orders],
  );
  useWakeLock(hasActiveDelivery && isDriver && !isPreviewMode);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyKit = useCallback(async () => {
    if (!isDriver || !kitsEnabled) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/kits/me`, { withCredentials: true });
      setMyKit(data?.kit || null);
    } catch (_) {
      setMyKit(null);
    }
  }, [isDriver, kitsEnabled]);

  useEffect(() => {
    fetchOrders();
    fetchMyKit();
    // Poll every 15 seconds for new assignments
    const interval = setInterval(() => {
      fetchOrders();
      fetchMyKit();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders, fetchMyKit]);

  // Cleanup tracking on unmount
  useEffect(() => {
    return () => {
      Object.values(trackingIntervals.current).forEach(clearInterval);
      Object.values(watchIds.current).forEach(id => {
        if (navigator.geolocation) navigator.geolocation.clearWatch(id);
      });
    };
  }, []);

  // Auto-start tracking for out_for_delivery orders
  useEffect(() => {
    orders.forEach(order => {
      if (order.status === 'out_for_delivery' && !trackingActive[order.id]) {
        startAutoTracking(order.id);
      }
      if (order.status !== 'out_for_delivery' && trackingActive[order.id]) {
        stopAutoTracking(order.id);
      }
    });
  }, [orders]);

  // Feb 2026 (Phase B) — Foreground catch-up POST + SW-ping bridge.
  // On `visibilitychange → visible` OR when the service worker fans out a
  // LOCATION_PING_REQUEST, immediately grab a fresh position and PATCH it
  // to every active order. Closes the OS-suspended location gap so
  // dispatch sees "back online at HH:MM" the instant the driver reopens
  // the app. `catch_up=true` lets the backend flag the gap in the timeline
  // (see /app/backend/app/routes/orders.py — accepted as an optional field
  // for auditing; unknown fields are silently ignored, so this is safe
  // even if the backend hasn't been redeployed yet).
  useEffect(() => {
    if (!isDriver || isPreviewMode) return undefined;
    const catchUpPing = async (reason = 'visibility') => {
      if (!('geolocation' in navigator)) return;
      const activeOrderIds = orders.filter(o => o.status === 'out_for_delivery').map(o => o.id);
      if (!activeOrderIds.length) return;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          await Promise.all(activeOrderIds.map((orderId) => (
            axios.patch(
              `${API_URL}/api/orders/${orderId}/location`,
              { latitude, longitude, catch_up: true, catch_up_reason: reason },
              { withCredentials: true },
            ).catch(() => { /* interceptor handles retry; failure is not fatal here */ })
          )));
          setLastLocation(prev => activeOrderIds.reduce(
            (acc, id) => ({ ...acc, [id]: { lat: latitude, lng: longitude, accuracy, time: new Date() } }),
            { ...prev },
          ));
        },
        () => { /* denied / timeout — silent by design */ },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
      );
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') catchUpPing('visibility');
    };
    const onSWMessage = (ev) => {
      if (ev.data && ev.data.type === 'LOCATION_PING_REQUEST') {
        catchUpPing(ev.data.reason || 'sw-ping');
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSWMessage);
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSWMessage);
      }
    };
  }, [isDriver, isPreviewMode, orders]);

  const sendLocation = async (orderId, lat, lng, accuracy) => {
    if (isPreviewMode) return; // never POST a real location while previewing
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/location`, {
        latitude: lat,
        longitude: lng
      }, { withCredentials: true });
      
      setLastLocation(prev => ({
        ...prev,
        [orderId]: { lat, lng, accuracy, time: new Date() }
      }));
    } catch (err) {
      console.error('Failed to send location:', err);
    }
  };

  const startAutoTracking = (orderId) => {
    if (trackingIntervals.current[orderId] || watchIds.current[orderId]) return; // Already tracking

    if (!('geolocation' in navigator)) {
      toast.error('Geolocation not supported');
      return;
    }

    const geoOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    // Feb 2026 — Latency pass. Switched from `setInterval(getCurrentPosition, 5000)`
    // to `watchPosition` + a client-side movement/time gate.
    // Rationale:
    //   * Old code hammered the geolocation stack every 5 s and POSTed to the
    //     server regardless of whether the driver moved. On a mid-range phone
    //     that's non-trivial battery drain + a ping per red light.
    //   * `watchPosition` yields only when the OS detects a real position
    //     change, so the callback rate matches the driver's actual movement.
    //   * The 50 m / 15 s gate mirrors the backend's ETA-recompute threshold
    //     — sending anything below that just costs bandwidth without changing
    //     the customer's map dot noticeably.
    //   * A 60 s "keepalive" ceiling still POSTs periodically even when the
    //     driver is parked, so dispatch never sees a driver dot go stale
    //     mid-delivery.
    const MOVEMENT_THRESHOLD_M = 50;
    const MIN_INTERVAL_MS = 15_000;   // fastest cadence when moving
    const KEEPALIVE_MS = 60_000;      // slowest cadence when stationary
    const EARTH_RADIUS_M = 6_371_000;
    const haversineM = (a, b) => {
      const toRad = (v) => (v * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
    };

    let lastSent = null;        // {lat, lng, ts}
    let inFlight = false;       // simple back-pressure guard
    const trySend = async (lat, lng, accuracy) => {
      // Back-pressure: if a PATCH is still in flight, drop this tick.
      // The next watchPosition callback will still fire on the next real
      // movement, so we never miss a "moved a lot" event.
      if (inFlight) return;
      const now = Date.now();
      if (lastSent) {
        const dist = haversineM(lastSent, { lat, lng });
        const dt = now - lastSent.ts;
        // Skip if neither the movement gate nor the keepalive tick warrants it.
        if (dist < MOVEMENT_THRESHOLD_M && dt < KEEPALIVE_MS) return;
        // Even when moving, respect the minimum interval so we don't
        // flood the server on high-frequency GPS bursts.
        if (dt < MIN_INTERVAL_MS && dist < MOVEMENT_THRESHOLD_M * 4) return;
      }
      inFlight = true;
      try {
        await sendLocation(orderId, lat, lng, accuracy);
        lastSent = { lat, lng, ts: now };
      } finally {
        inFlight = false;
      }
    };

    // Initial location send — seeds `lastSent` and gives dispatch an
    // immediate dot on the map without waiting for the driver to move.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng, accuracy } = position.coords;
        sendLocation(orderId, lat, lng, Math.round(accuracy));
        lastSent = { lat, lng, ts: Date.now() };
        toast.success('Auto-tracking started', {
          description: 'Location updates on movement (50 m) or every 60 s',
        });
      },
      () => {
        toast.error('Could not get initial location');
      },
      geoOptions,
    );

    // watchPosition fires on OS-detected movement. The gate in trySend
    // decides whether that movement is worth a network round trip.
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude: lat, longitude: lng, accuracy } = position.coords;
        trySend(lat, lng, Math.round(accuracy));
      },
      (error) => {
        console.error('Location error:', error);
      },
      geoOptions,
    );

    // Keepalive: even when the OS never fires a callback (driver
    // parked, phone in pocket), tick every KEEPALIVE_MS so dispatch's
    // "driver online" indicator stays green.
    const keepaliveId = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: lng, accuracy } = position.coords;
          trySend(lat, lng, Math.round(accuracy));
        },
        () => {},
        geoOptions,
      );
    }, KEEPALIVE_MS);

    watchIds.current[orderId] = watchId;
    trackingIntervals.current[orderId] = keepaliveId;
    setTrackingActive((prev) => ({ ...prev, [orderId]: true }));
  };

  const stopAutoTracking = (orderId) => {
    if (trackingIntervals.current[orderId]) {
      clearInterval(trackingIntervals.current[orderId]);
      delete trackingIntervals.current[orderId];
    }
    if (watchIds.current[orderId]) {
      navigator.geolocation.clearWatch(watchIds.current[orderId]);
      delete watchIds.current[orderId];
    }
    setTrackingActive(prev => ({ ...prev, [orderId]: false }));
    setLastLocation(prev => {
      const newState = { ...prev };
      delete newState[orderId];
      return newState;
    });
  };

  const toggleTracking = (orderId) => {
    if (trackingActive[orderId]) {
      stopAutoTracking(orderId);
      toast.info('Auto-tracking stopped');
    } else {
      startAutoTracking(orderId);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    if (isPreviewMode) { previewBlock('status updates'); return; }
    // Stop tracking when marking as delivered
    if (newStatus === 'delivered') {
      stopAutoTracking(orderId);
    }
    
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Order marked as ${newStatus.replace('_', ' ')}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  // Phase 5.5-Lite — fetch payment settings for the driver-side routing badge.
  useEffect(() => {
    if (!isDriver) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/payment-settings`, { withCredentials: true });
        setPaymentRouting(data);
      } catch (_) {
        /* non-critical */
      }
    })();
  }, [isDriver]);

  const openPaymentQR = async (order) => {
    if (isPreviewMode) { previewBlock('payment QR'); return; }
    setQrOrder({ id: order.id, total: order.total, status: order.status });
    setQrOpen(true);
    setQrLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/orders/${order.id}/payment-render`,
        { withCredentials: true },
      );
      setQrRender(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load payment QR');
      setQrRender({ configured: false });
    } finally {
      setQrLoading(false);
    }
  };

  const markPaid = async (orderId, alsoDeliver) => {
    if (isPreviewMode) { previewBlock('mark paid'); return; }
    if (alsoDeliver) stopAutoTracking(orderId);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/orders/${orderId}/payment`,
        { status: 'paid', also_mark_delivered: !!alsoDeliver },
        { withCredentials: true },
      );
      toast.success(data.delivered_now ? 'Marked PAID & DELIVERED' : 'Marked PAID');
      setQrOpen(false);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark payment');
    }
  };

  const activeOrders = orders.filter(o => ['confirmed', 'preparing', 'out_for_delivery'].includes(o.status));
  const completedOrders = orders.filter(o => o.status === 'delivered');

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      preparing: 'bg-[rgba(255,159,10,0.1)] text-[#FF9F0A] border-[#FF9F0A]',
      out_for_delivery: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      delivered: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]'
    };
    return styles[status] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-[#39FF14] font-mono">
            <span className="cursor-blink">█</span> LOADING_ASSIGNMENTS...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003] overflow-x-hidden" data-testid="driver-dashboard">
      <Navbar />

      {/* Phase 5.9.21 — VIEW AS DRIVER preview banner */}
      {isPreviewMode && (
        <div
          className="sticky top-0 z-40 bg-[#FF9F0A] text-[#050003] border-b-2 border-[#050003] px-4 py-2 flex items-center justify-between gap-3"
          data-testid="driver-preview-banner"
        >
          <span className="font-mono text-[10px] sm:text-xs uppercase tracking-wider font-bold">
            ⚠ PREVIEW MODE — VIEWING AS A DRIVER (YOUR OPERATOR SESSION IS INTACT)
          </span>
          <Link
            to="/dispatch"
            className="font-mono text-[10px] sm:text-xs uppercase tracking-wider font-bold underline shrink-0 hover:text-[#050003]/70"
            data-testid="driver-preview-return"
          >
            ← Return to Dispatch
          </Link>
        </div>
      )}

      {/* Feb 2026 — sticky "Keep Screen On" banner. Renders ONLY when the
          driver has at least one out_for_delivery order and they aren't in
          preview mode. Explains the browser-security limitation to first-time
          drivers so they understand WHY we auto-hold the wake lock. */}
      {hasActiveDelivery && !isPreviewMode && (
        <div
          className="sticky top-0 z-40 bg-[#FF9F0A] text-[#050003] border-b-2 border-[#050003] px-4 py-2 flex items-center gap-2"
          data-testid="driver-keep-screen-on-banner"
          role="status"
        >
          <span className="text-lg leading-none" aria-hidden="true">📱</span>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[11px] sm:text-xs uppercase tracking-wider font-bold">
              Keep screen on during delivery
            </div>
            <div className="font-mono text-[10px] text-[#050003]/80 leading-tight">
              Wake lock is holding the screen. If you background the app, GPS pings pause and your Health Score drops.
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header — stacks on mobile so the action buttons can wrap below the title.
            Phase 5.9.8 — was a single flex row that overflowed off-screen on phones. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div className="min-w-0">
            <h1 className="text-[#39FF14] font-mono text-xl sm:text-2xl font-bold neon-text truncate">
              {'>'} DRIVER_TERMINAL
            </h1>
            <p className="text-[#4B6B52] font-mono text-xs sm:text-sm mt-1 truncate">
              // WELCOME, {user?.name?.toUpperCase()}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <PushSubscribeButton testId="driver-push-toggle" />
            <Link to="/staff/rewards" data-testid="driver-rewards-link">
              <Button variant="outline" className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none px-3 sm:px-4">
                <QrCode className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">MY QR</span>
              </Button>
            </Link>
            <Link to="/driver/profile" data-testid="driver-profile-link">
              <Button variant="outline" className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none px-3 sm:px-4">
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">PROFILE</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
            <p className="text-[#6B8E73] font-mono text-xs uppercase">Active</p>
            <p className="text-[#39FF14] font-mono text-2xl font-bold">{activeOrders.length}</p>
          </div>
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
            <p className="text-[#6B8E73] font-mono text-xs uppercase">Completed Today</p>
            <p className="text-[#39FF14] font-mono text-2xl font-bold">{completedOrders.length}</p>
          </div>
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
            <p className="text-[#6B8E73] font-mono text-xs uppercase">Total Orders</p>
            <p className="text-[#39FF14] font-mono text-2xl font-bold">{orders.length}</p>
          </div>
          <div className={`bg-[#0C0612] border p-4 ${heartbeatEnabled && heartbeatOnline ? 'border-[#39FF14]' : 'border-[#FF9F0A]'}`} data-testid="driver-online-card">
            <p className="text-[#6B8E73] font-mono text-xs uppercase">Status</p>
            <div className="flex flex-col gap-2 mt-1 sm:flex-row sm:items-center sm:justify-between">
              <p className={`font-mono text-sm sm:text-lg font-bold flex items-center gap-2 ${heartbeatEnabled && heartbeatOnline ? 'text-[#39FF14]' : 'text-[#FF9F0A]'}`}>
                <span className={`w-2 h-2 rounded-full ${heartbeatEnabled && heartbeatOnline ? 'bg-[#39FF14] status-pulse' : 'bg-[#FF9F0A]'}`} />
                {heartbeatEnabled && heartbeatOnline ? 'ONLINE' : 'OFFLINE'}
              </p>
              <Button
                size="sm"
                onClick={() => { if (isPreviewMode) { previewBlock('go online'); return; } toggleOnline(); }}
                disabled={isPreviewMode}
                className={`h-7 w-full sm:w-auto rounded-none font-mono text-xs ${heartbeatEnabled ? 'bg-[rgba(255,159,10,0.15)] text-[#FF9F0A] border border-[#FF9F0A] hover:bg-[rgba(255,159,10,0.25)]' : 'bg-[#39FF14] text-[#050003] hover:bg-[#4AF626]'} disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid="toggle-online-btn"
              >
                {heartbeatEnabled ? (<><PowerOff className="w-3 h-3 mr-1" />GO OFFLINE</>) : (<><Power className="w-3 h-3 mr-1" />GO ONLINE</>)}
              </Button>
            </div>
            {heartbeatLoc && heartbeatEnabled && (
              <p className="text-[#4B6B52] font-mono text-[10px] mt-2 truncate">
                {Number(heartbeatLoc.lat ?? 0).toFixed(4)}, {Number(heartbeatLoc.lng ?? 0).toFixed(4)} · {heartbeatLoc.time.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>

        {/* Phase 5.5-Lite — payment routing badge for the driver (super-admin toggle) */}
        {paymentRouting && paymentRouting.show_routing_to_drivers && paymentRouting.cashapp_handle && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] px-4 py-2 flex items-center justify-between" data-testid="driver-payment-routing">
            <span className="text-[#6B8E73] font-mono text-xs uppercase">// CASH APP PAYMENTS ROUTE TO</span>
            <span className="text-[#39FF14] font-mono text-xs">{user?.cashapp_handle || paymentRouting.cashapp_handle}</span>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="active" className="space-y-6">
          {/* Phase 5.9.8 — tabs list now allows horizontal scroll on narrow
              screens instead of clipping the leftmost tab. */}
          <TabsList className="bg-[#0C0612] border border-[#2A0F3D] p-1 rounded-none w-full overflow-x-auto justify-start flex-nowrap">
            <TabsTrigger 
              value="active" 
              className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono shrink-0 whitespace-nowrap"
              data-testid="tab-active"
            >
              <Truck className="w-4 h-4 mr-2" />
              ACTIVE ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono shrink-0 whitespace-nowrap"
              data-testid="tab-completed"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              COMPLETED ({completedOrders.length})
            </TabsTrigger>
            {kitsEnabled && (
              <TabsTrigger
                value="kit"
                className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono shrink-0 whitespace-nowrap"
                data-testid="tab-mykit"
              >
                <Package className="w-4 h-4 mr-2" />
                MY KIT
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="active" className="space-y-4">
            {activeOrders.length === 0 ? (
              <div className="bg-[#0C0612] border border-[#2A0F3D] p-12 text-center">
                <Truck className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                <p className="text-[#4B6B52] font-mono">// NO ACTIVE DELIVERIES</p>
                <p className="text-[#4B6B52] font-mono text-sm mt-2">Waiting for dispatch assignments...</p>
              </div>
            ) : (
              activeOrders.map(order => (
                <DeliveryCard 
                  key={order.id} 
                  order={order} 
                  onStatusUpdate={handleStatusUpdate}
                  isTracking={trackingActive[order.id]}
                  lastLocation={lastLocation[order.id]}
                  onToggleTracking={toggleTracking}
                  onShowPaymentQR={openPaymentQR}
                  onMarkPaid={markPaid}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {completedOrders.length === 0 ? (
              <div className="bg-[#0C0612] border border-[#2A0F3D] p-12 text-center">
                <CheckCircle className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                <p className="text-[#4B6B52] font-mono">// NO COMPLETED DELIVERIES</p>
              </div>
            ) : (
              completedOrders.map(order => (
                <div 
                  key={order.id} 
                  className="bg-[#0C0612] border border-[#2A0F3D] p-4"
                  data-testid={`completed-order-${order.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#39FF14] font-mono font-bold">ORDER #{order.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-[#4B6B52] font-mono text-xs">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#39FF14] font-mono">${Number(order.total || 0).toFixed(2)}</p>
                      <p className="text-[#4B6B52] font-mono text-xs">
                        {formatOrderTimestamp(order.updated_at)}
                      </p>
                    </div>
                  </div>
                  {/* Feb 2026 — completed-order Health Score chip so drivers
                      can see per-trip GPS coverage after the fact. Skips
                      when no health data (legacy orders / short trips). */}
                  {order.delivery_health && (
                    <div
                      className={`mt-3 flex items-center gap-2 border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                        order.delivery_health.status === 'healthy'
                          ? 'border-[#39FF14]/40 text-[#39FF14] bg-[#39FF14]/5'
                          : order.delivery_health.status === 'degraded'
                            ? 'border-[#FF9F0A]/40 text-[#FF9F0A] bg-[#FF9F0A]/5'
                            : 'border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5'
                      }`}
                      data-testid={`driver-completed-health-${order.id}`}
                    >
                      <span>📡</span>
                      <span className="font-bold">{order.delivery_health.score}% GPS</span>
                      <span className="text-[#4B6B52]/90">
                        · {order.delivery_health.ping_count} pings
                        {order.delivery_health.catch_up_count > 0 && ` · ${order.delivery_health.catch_up_count} wake-catchup`}
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          {kitsEnabled && (
            <TabsContent value="kit" className="space-y-4">
              {!myKit ? (
                <div className="bg-[#0C0612] border border-[#2A0F3D] p-12 text-center">
                  <Package className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                  <p className="text-[#4B6B52] font-mono">// NO KIT ASSIGNED</p>
                  <p className="text-[#4B6B52] font-mono text-sm mt-2">
                    Visit the warehouse to pick up your kit before going online.
                  </p>
                </div>
              ) : (
                <div className={`bg-[#0C0612] border p-6 ${myKit.mode === 'restock' ? 'border-[#00F0FF]' : 'border-[#39FF14]'}`} data-testid="my-kit-card">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <h2 className={`font-mono font-bold text-lg ${myKit.mode === 'restock' ? 'text-[#00F0FF]' : 'text-[#39FF14]'}`}>
                        {myKit.mode === 'restock' ? '📦 RESTOCK MODE' : '🚚 DELIVERY MODE'}
                      </h2>
                      <p className="text-[#4B6B52] font-mono text-xs mt-1">
                        Opened {myKit.opened_at ? new Date(myKit.opened_at).toLocaleString() : ''} ·
                        Total kit value: <span className="text-[#39FF14]">${kitTotalValue(myKit.items).toFixed(2)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {myKit.items.map(item => (
                      <KitItemBar key={item.product_id} item={item} />
                    ))}
                  </div>
                  <p className="text-[#4B6B52] font-mono text-[10px] mt-4 pt-3 border-t border-[#2A0F3D]">
                    // Kit auto-decrements as you mark deliveries complete.
                    {myKit.mode === 'restock' && ' Restock mode: dispatcher will route you to drivers needing items.'}
                  </p>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
      {/* Phase 5.5-Lite — Driver-side payment QR modal */}
      <DriverPaymentQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        amount={qrOrder?.total || 0}
        orderShortId={(qrOrder?.id || '').slice(0, 8).toUpperCase()}
        render={qrRender}
        loading={qrLoading}
        onMarkPaidAndDelivered={
          qrOrder && qrOrder.status === 'out_for_delivery'
            ? () => markPaid(qrOrder.id, true)
            : null
        }
        onMarkPaidOnly={
          qrOrder && qrOrder.status !== 'out_for_delivery'
            ? () => markPaid(qrOrder.id, false)
            : null
        }
      />
    </div>
  );
}

function DeliveryCard({ order, onStatusUpdate, isTracking, lastLocation, onToggleTracking, onShowPaymentQR, onMarkPaid }) {
  const tapToCallEnabled = useFlag('driver.tap_to_call', false);
  const openInMapsEnabled = useFlag('driver.open_in_maps', false);

  const mapsHref = (() => {
    if (!order.delivery_address) return null;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isApple = /iPad|iPhone|iPod|Mac/.test(ua);
    const q = encodeURIComponent(order.delivery_address);
    return isApple
      ? `https://maps.apple.com/?daddr=${q}`
      : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
  })();

  const cleanedPhone = order.customer_phone ? order.customer_phone.replace(/[^\d+]/g, '') : '';
  const canCall = tapToCallEnabled && !!cleanedPhone;
  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      preparing: 'bg-[rgba(255,159,10,0.1)] text-[#FF9F0A] border-[#FF9F0A]',
      out_for_delivery: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]'
    };
    return styles[status] || '';
  };

  return (
    <div className="bg-[#0C0612] border border-[#39FF14] p-6" data-testid={`active-order-${order.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-[#39FF14]" />
            <h3 className="text-[#39FF14] font-mono font-bold">
              ORDER #{order.id.slice(0, 8).toUpperCase()}
            </h3>
          </div>
          <p className="text-[#4B6B52] font-mono text-sm mt-1">
            {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <Badge className={`${getStatusBadge(order.status)} border rounded-none font-mono text-xs uppercase`}>
          {formatOrderStatus(order.status)}
        </Badge>
      </div>

      {/* Feb 2026 — Delivery Health chip on active out-for-delivery orders.
          Renders IN-FLIGHT while the driver is on the way so they get live
          feedback on their GPS coverage. Green ≥ 85%, orange 60-84%, red < 60%. */}
      {order.status === 'out_for_delivery' && order.delivery_health && (
        <div
          className={`mb-4 flex items-center gap-2 border px-3 py-2 font-mono text-xs uppercase tracking-widest ${
            order.delivery_health.status === 'healthy'
              ? 'border-[#39FF14]/40 text-[#39FF14] bg-[#39FF14]/5'
              : order.delivery_health.status === 'degraded'
                ? 'border-[#FF9F0A]/40 text-[#FF9F0A] bg-[#FF9F0A]/5'
                : 'border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5'
          }`}
          data-testid={`driver-active-health-${order.id}`}
        >
          <span>📡</span>
          <span className="font-bold">{order.delivery_health.score}% GPS COVERAGE</span>
          <span className="text-[#4B6B52]/90 ml-auto">
            {order.delivery_health.ping_count} pings
          </span>
        </div>
      )}

      {/* Live Tracking Status - shown when out for delivery */}
      {order.status === 'out_for_delivery' && (
        <div className={`mb-4 p-3 border ${isTracking ? 'bg-[rgba(57,255,20,0.1)] border-[#39FF14]' : 'bg-[rgba(255,159,10,0.1)] border-[#FF9F0A]'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isTracking ? (
                <>
                  <Radio className="w-4 h-4 text-[#39FF14] animate-pulse" />
                  <span className="text-[#39FF14] font-mono text-xs uppercase">LIVE TRACKING ACTIVE</span>
                </>
              ) : (
                <>
                  <RadioReceiver className="w-4 h-4 text-[#FF9F0A]" />
                  <span className="text-[#FF9F0A] font-mono text-xs uppercase">TRACKING PAUSED</span>
                </>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleTracking(order.id)}
              className={`h-6 px-2 rounded-none text-xs ${isTracking ? 'text-[#FF9F0A] hover:bg-[rgba(255,159,10,0.1)]' : 'text-[#39FF14] hover:bg-[rgba(57,255,20,0.1)]'}`}
              data-testid={`toggle-tracking-${order.id}`}
            >
              {isTracking ? 'PAUSE' : 'RESUME'}
            </Button>
          </div>
          {lastLocation && (
            <div className="mt-2 text-[#A3C7A8] font-mono text-xs">
              <MapPin className="w-3 h-3 inline mr-1" />
              {Number(lastLocation.lat ?? 0).toFixed(5)}, {Number(lastLocation.lng ?? 0).toFixed(5)} 
              <span className="text-[#4B6B52] ml-2">
                (±{lastLocation.accuracy}m) • {lastLocation.time.toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Customer Info */}
      <div className="bg-[#050003] border border-[#2A0F3D] p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-4 h-4 text-[#6B8E73] shrink-0" />
            <span className="text-[#A3C7A8] font-mono text-sm truncate">{order.customer_name}</span>
          </div>
          {tapToCallEnabled && (
            canCall ? (
              <a
                href={`tel:${cleanedPhone}`}
                className="flex items-center gap-1 px-2 py-1 border border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] font-mono text-xs"
                data-testid={`call-customer-${order.id}`}
                title={`Call ${order.customer_name}`}
              >
                <Phone className="w-3 h-3" />
                CALL
              </a>
            ) : (
              <span
                className="flex items-center gap-1 px-2 py-1 border border-[#2A0F3D] text-[#4B6B52] font-mono text-xs cursor-not-allowed"
                title="Customer has no phone on file"
              >
                <Phone className="w-3 h-3" />
                NO PHONE
              </span>
            )
          )}
        </div>
        <div className="flex items-start gap-2 mb-3">
          <MapPin className="w-4 h-4 text-[#6B8E73] mt-0.5 shrink-0" />
          <span className="text-[#A3C7A8] font-mono text-sm">{order.delivery_address}</span>
        </div>
        {openInMapsEnabled && mapsHref && (
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[rgba(0,240,255,0.08)] font-mono text-xs uppercase tracking-wider"
            data-testid={`open-in-maps-${order.id}`}
            title="Open turn-by-turn navigation"
          >
            <Navigation className="w-4 h-4" />
            Open in {/Apple|iPad|iPhone|iPod|Mac/.test(typeof navigator !== 'undefined' ? navigator.userAgent : '') ? 'Apple Maps' : 'Google Maps'}
          </a>
        )}
      </div>

      {/* Order Items */}
      <div className="mb-4">
        <p className="text-[#6B8E73] font-mono text-xs uppercase mb-2">ORDER_ITEMS</p>
        <div className="space-y-1">
          {order.items.map((item, index) => (
            <div key={index} className="flex justify-between items-center text-[#A3C7A8] font-mono text-sm" data-testid={`driver-order-item-${index}`}>
              <span className="flex items-center gap-2 flex-wrap">
                <span>{item.quantity}x {item.product_name}</span>
                {item.size && (
                  <span
                    className="px-2 py-0.5 text-xs font-bold uppercase tracking-wider border border-[#FFD60A] text-[#FFD60A] bg-[rgba(255,214,10,0.08)] rounded-none"
                    data-testid={`driver-order-item-size-${index}`}
                  >
                    {item.size}
                  </span>
                )}
              </span>
              <span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between text-[#39FF14] font-mono font-bold pt-2 border-t border-[#2A0F3D]">
            <span>TOTAL</span>
            <span>${Number(order.total || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Info */}
      <div className="mb-4">
        <p className="text-[#6B8E73] font-mono text-xs">
          Payment: <span className="text-[#FF9F0A] uppercase">{order.payment_method}</span>
        </p>
        {order.notes && (
          <p className="text-[#6B8E73] font-mono text-xs mt-1">
            Notes: <span className="text-[#A3C7A8]">{order.notes}</span>
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-4 border-t border-[#2A0F3D]">
        {/* Payment status badge */}
        {order.payment_method && order.payment_method !== 'cash' && (
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-[#6B8E73] uppercase">PAYMENT</span>
            <span className={
              order.payment_status === 'paid'
                ? 'text-[#39FF14] uppercase'
                : 'text-[#FF9F0A] uppercase'
            }>
              {order.payment_status === 'paid' ? '✓ PAID' : '⌛ PENDING · ' + (order.payment_method || '').toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex gap-2">
          {/* Show payment QR for unpaid Cash App on-delivery orders */}
          {order.payment_method === 'cashapp_on_delivery' && order.payment_status !== 'paid' && onShowPaymentQR && (
            <Button
              onClick={() => onShowPaymentQR(order)}
              variant="outline"
              className="flex-1 bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050003] font-mono uppercase rounded-none"
              data-testid={`driver-show-qr-${order.id}`}
            >
              <QrCode className="w-4 h-4 mr-2" />
              SHOW QR
            </Button>
          )}
          {order.status === 'confirmed' || order.status === 'preparing' ? (
            <Button
              onClick={() => onStatusUpdate(order.id, 'out_for_delivery')}
              className="flex-1 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none"
              data-testid={`start-delivery-${order.id}`}
            >
              <Truck className="w-4 h-4 mr-2" />
              START DELIVERY
            </Button>
          ) : order.status === 'out_for_delivery' ? (
            order.payment_status !== 'paid' && order.payment_method !== 'cashapp_now' && onMarkPaid ? (
              <Button
                onClick={() => onMarkPaid(order.id, true)}
                className="flex-1 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none"
                data-testid={`mark-paid-delivered-${order.id}`}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                MARK PAID &amp; DELIVERED
              </Button>
            ) : (
              <Button
                onClick={() => onStatusUpdate(order.id, 'delivered')}
                className="flex-1 bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none"
                data-testid={`mark-delivered-${order.id}`}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                MARK DELIVERED
              </Button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
